/**
 * The ensembles an arrangement is most often asked for, each as the seats it is made of.
 *
 * Arrange and the Lab board offer them as one-click rosters, and asking in words (`theory/ask`) tells
 * the model what each is made of, so a request that names one is read the same way the
 * menu would seat it.
 */

/** The piano, as the generator names it: a pianist's two hands on a grand staff. */
export const PIANO = "piano";

/** The harp, as the generator names it: a harpist's two hands on a grand staff. */
export const HARP = "harp";

/**
 * The organ, as the generator names it: an organist's two hands on its manuals, and the feet
 * of the lowest seated on the pedal-board, a third staff under them.
 */
export const ORGAN = "organ";

/** The celesta, as the generator names it: its player's two hands on a grand staff. */
export const CELESTA = "celesta";

/** A player of two hands: the name its chip carries and what a hand of its holds. */
export interface Handed {
  readonly instrument: string;
  readonly label: string;
  /** Its family, as `rk_instruments.INSTRUMENT_FAMILY` names it. */
  readonly family: string;
  /** As `rk_keep_piano_reach.Handed` sets it, in words. */
  readonly reach: string;
}

/** The book's instruments of little sustaining power, the plucked and the struck. */
const STRUCK = "little_sustaining";

/** A pianist's hand, an organist's and a celesta player's. */
const NINTH = "five notes to a hand within a ninth";

/**
 * The players of two hands. None is one of the generator's seats: each is arranged for, alone
 * or beside any players, where its hands play what they were not seated for, and where a
 * roster has more than one, each on a grand staff of its own.
 */
export const HANDED: readonly Handed[] = [
  { instrument: PIANO, label: "Piano", family: STRUCK, reach: NINTH },
  { instrument: HARP, label: "Harp", family: STRUCK, reach: "four notes to a hand within a tenth" },
  { instrument: ORGAN, label: "Organ", family: "other", reach: NINTH },
  { instrument: CELESTA, label: "Celesta", family: STRUCK, reach: NINTH },
];

/** The kinds of player of two hands `roster` seats, each once however many play it. */
export function handedIn(roster: readonly string[]): Handed[] {
  return HANDED.filter((one) => roster.includes(one.instrument));
}

/** The guitar, as the generator names it: one player who sounds a chord under a tune. */
export const GUITAR = "guitar";

/** The bowed strings, as the generator names them: the players who sound two notes at once. */
export const BOWED: readonly string[] = ["violin", "viola", "cello", "double_bass"];

/** The rosters worth one click. Every value is a seat the pipeline knows by that name. */
export const ENSEMBLES: ReadonlyArray<{ name: string; roster: readonly string[] }> = [
  { name: "Piano", roster: [PIANO] },
  { name: "String quartet", roster: ["violin", "violin", "viola", "cello"] },
  { name: "String quintet", roster: ["violin", "violin", "viola", "cello", "double_bass"] },
  { name: "Wind quintet", roster: ["flute", "oboe", "clarinet", "horn", "bassoon"] },
  { name: "Brass quartet", roster: ["horn", "trumpet", "trombone", "tuba"] },
  {
    name: "Saxophone quartet",
    roster: ["soprano_saxophone", "alto_saxophone", "tenor_saxophone", "baritone_saxophone"],
  },
  // The wind and strings of Beethoven's First and of Brahms's First, without their timpani.
  // No piccolo: seating hands the tune to the player lying highest, and it would take it.
  {
    name: "Classical orchestra",
    roster: [
      ...["flute", "flute", "oboe", "oboe", "clarinet", "clarinet", "bassoon", "bassoon"],
      ...["horn", "horn", "trumpet", "trumpet"],
      ...["violin", "violin", "viola", "cello", "double_bass"],
    ],
  },
  {
    name: "Romantic orchestra",
    roster: [
      ...["flute", "flute", "oboe", "oboe", "clarinet", "clarinet", "bassoon", "bassoon"],
      "contrabassoon",
      ...["horn", "horn", "horn", "horn", "trumpet", "trumpet", "trombone", "trombone", "trombone"],
      ...["violin", "violin", "viola", "cello", "double_bass"],
    ],
  },
];
